import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { Breadcrumbs, Link as MuiLink, Typography } from '@mui/material';
import { Link, useMatches } from '@tanstack/react-router';
import type { BreadcrumbPath, RouteBreadcrumbPathHook, RouteLabelHook } from '../services/routing/routeMetadata';

const BreadcrumbLabel = ({ useLabel }: { useLabel: RouteLabelHook }) => useLabel();

const BreadcrumbLink = ({ to, useLabel }: { to: BreadcrumbPath; useLabel: RouteLabelHook }) => (
  <MuiLink component={Link} to={to} underline="hover" color="inherit" variant="body2">
    <BreadcrumbLabel useLabel={useLabel} />
  </MuiLink>
);

const DynamicBreadcrumbLink = ({ useLabel, useTo }: { useLabel: RouteLabelHook; useTo: RouteBreadcrumbPathHook }) => {
  const to = useTo();
  return to ? (
    <BreadcrumbLink to={to} useLabel={useLabel} />
  ) : (
    <Typography color="text.primary" variant="body2">
      <BreadcrumbLabel useLabel={useLabel} />
    </Typography>
  );
};

const useAppBreadcrumbs = () =>
  useMatches({
    select: (matches) =>
      matches.flatMap((match) => {
        const parents = (match.staticData.parentBreadcrumbs ?? []).map((breadcrumb, index) => ({
          ...breadcrumb,
          key: `${match.id}-parent-${index}`,
        }));
        const useLabel = match.staticData.useBreadcrumbLabel;
        if (!useLabel) {
          return parents;
        }
        return [
          ...parents,
          {
            key: match.id,
            useLabel,
            to: match.staticData.breadcrumbTo,
            useTo: match.staticData.useBreadcrumbTo,
          },
        ];
      }),
  });

export const AppBreadcrumbs = () => {
  const breadcrumbs = useAppBreadcrumbs();

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs separator={<NavigateNextRoundedIcon fontSize="small" />} aria-label="Breadcrumb" sx={{ mb: 3 }}>
      {breadcrumbs.map((breadcrumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const label = <BreadcrumbLabel useLabel={breadcrumb.useLabel} />;
        if (isLast || (!breadcrumb.to && !breadcrumb.useTo)) {
          return (
            <Typography key={breadcrumb.key} color="text.primary" variant="body2">
              {label}
            </Typography>
          );
        }
        if (breadcrumb.useTo) {
          return <DynamicBreadcrumbLink key={breadcrumb.key} useLabel={breadcrumb.useLabel} useTo={breadcrumb.useTo} />;
        }
        if (breadcrumb.to) {
          return <BreadcrumbLink key={breadcrumb.key} to={breadcrumb.to} useLabel={breadcrumb.useLabel} />;
        }
        return null;
      })}
    </Breadcrumbs>
  );
};
